import type { DefaultError, QueryClient } from "@tanstack/query-core";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { type ReactNode, useCallback } from "react";
import { useSnackbar } from "src/notifications/SnackbarProvider.ts";
import type { UseMutateFunction } from "@tanstack/react-query";

export const useErpMutation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> & {
    errorMessage?: ReactNode;
    successMessage?: ReactNode;
  },
  queryClient?: QueryClient,
) => {
  const { successMessage, errorMessage, ...restOptions } = options;
  const snackbar = useSnackbar();
  const mutation = useMutation<TData, TError, TVariables, TContext>(
    restOptions,
    queryClient,
  );

  const originalMutate = mutation.mutate;

  const patchedMutate: UseMutateFunction<TData, TError, TVariables, TContext> =
    useCallback(
      (variables, options): void => {
        originalMutate(variables, {
          ...options,
          onSuccess: (...args) => {
            if (successMessage) {
              snackbar.success({
                flash: true,
                message: successMessage,
              });
            }
            return options?.onSuccess?.(...args);
          },
          onError: (...args) => {
            if (errorMessage) {
              snackbar.error({
                flash: true,
                message: errorMessage,
              });
            }

            return options?.onError?.(...args);
          },
        });
      },
      [snackbar, successMessage, errorMessage, originalMutate],
    );

  mutation.mutate = patchedMutate;

  return mutation;
};
